import { endSession } from '@/lib/auth-session'

export interface SseJsonEvent<T = unknown> {
  event: string
  data: T
}

interface ReadSseJsonOptions {
  doneEvent?: string
  handlers?: Record<string, (data: unknown) => void>
}

function parseSseEvent(value: string): { event: string; data: string } | null {
  const lines = value.split(/\r?\n/)
  const data: string[] = []
  let event = 'message'

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
    }
    if (line.startsWith('data:')) {
      data.push(line.slice(5).trimStart())
    }
  }

  if (data.length === 0) return null
  return { event, data: data.join('\n') }
}

async function forEachSseEvent(response: Response, handleEvent: (event: string, data: unknown) => void): Promise<void> {
  if (!response.body) throw new Error('Missing response stream')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let separator = buffer.match(/\r?\n\r?\n/)
      while (separator?.index !== undefined) {
        const message = parseSseEvent(buffer.slice(0, separator.index))
        if (message) {
          if (message.event === 'session_revoked') {
            endSession()
            await reader.cancel()
            return
          }
          handleEvent(message.event, JSON.parse(message.data))
        }
        buffer = buffer.slice(separator.index + separator[0].length)
        separator = buffer.match(/\r?\n\r?\n/)
      }
    }

    buffer += decoder.decode()
    if (buffer.trim()) {
      const message = parseSseEvent(buffer)
      if (message) {
        handleEvent(message.event, JSON.parse(message.data))
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export async function readSseJson<T>(response: Response, options: ReadSseJsonOptions = {}): Promise<T> {
  const doneEvent = options.doneEvent ?? 'done'
  let result: T | undefined
  let seenDone = false

  await forEachSseEvent(response, (event, data) => {
    options.handlers?.[event]?.(data)
    if (event === doneEvent) {
      result = data as T
      seenDone = true
    }
  })

  if (!seenDone) throw new Error(`Missing ${doneEvent} event`)
  return result as T
}

export async function subscribeSse(
  response: Response,
  handlers: Record<string, (data: unknown) => void>,
): Promise<void> {
  await forEachSseEvent(response, (event, data) => {
    handlers[event]?.(data)
  })
}
